import { useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { fetchClient } from '../../../../api/typedClient'
import { MappingMutationParams, RemoveMappingParams } from '../types'
import { getApiType } from '../utils'

interface UseMappingOperationsProps {
  entityType: string
}

interface UseMappingOperationsReturn {
  createMappingMutation: ReturnType<typeof useMutation>
  removeMappingMutation: ReturnType<typeof useMutation>
}

/**
 * Hook for managing mapping CRUD operations
 */
export const useMappingOperations = ({
  entityType,
}: UseMappingOperationsProps): UseMappingOperationsReturn => {
  const queryClient = useQueryClient()

  // Create mapping mutation
  const createMappingMutation = useMutation({
    mutationFn: async ({
      tenantName,
      sourceId,
      goid,
      displayName,
      mappingId,
    }: MappingMutationParams) => {
      const { data, error } = await fetchClient.PUT(
        // @ts-expect-error TODO: Add /crossroads/mappings/{mapping_id} to OpenAPI spec
        `/crossroads/mappings/${mappingId}`,
        {
          body: {
            type: getApiType(entityType),
            source_id: sourceId,
            goids: [goid],
            display_name: displayName,
            source_system: 'Gravitate Supply & Dispatch',
            matching_info: {},
            extra_data: {},
          },
          headers: { 'X-Tenant-Name': tenantName },
        },
      )
      if (error) {
        throw new Error(`Failed to create mapping: ${JSON.stringify(error)}`)
      }
      return data
    },
    onSuccess: (_, variables) => {
      message.success('Mapping created successfully')
      // Invalidate tenant mappings for the specific tenant
      queryClient.invalidateQueries({
        queryKey: ['tenant-mappings', variables.tenantName, entityType],
      })
      // Invalidate all tenant mappings for this entity type to refresh both sides
      queryClient.invalidateQueries({
        queryKey: ['tenant-mappings'],
        predicate: (query) => {
          const [key1, , key3] = query.queryKey
          return key1 === 'tenant-mappings' && key3 === entityType
        },
      })
      // Invalidate MRD grid data to refresh the alignment
      queryClient.invalidateQueries({
        queryKey: ['crossroads-entity-grid', entityType],
      })
      // Invalidate companies data if it's a company type
      if (entityType === 'company' || entityType === 'counterparty') {
        queryClient.invalidateQueries({
          queryKey: ['crossroads-network-companies-3way'],
        })
      }
    },
    onError: (error: Error) => {
      message.error(`Failed to create mapping: ${error.message}`)
    },
  })

  // Remove mapping mutation
  const removeMappingMutation = useMutation({
    mutationFn: async ({ tenantName, mappingId }: RemoveMappingParams) => {
      const { data, error } = await fetchClient.DELETE(
        // @ts-expect-error TODO: Add /crossroads/mappings/{mapping_id} to OpenAPI spec
        `/crossroads/mappings/${mappingId}`,
        {
          headers: { 'X-Tenant-Name': tenantName },
        },
      )
      if (error) {
        throw new Error(`Failed to remove mapping: ${JSON.stringify(error)}`)
      }
      return data
    },
    onSuccess: (_, variables) => {
      message.success('Mapping removed successfully')
      // Invalidate tenant mappings for the specific tenant
      queryClient.invalidateQueries({
        queryKey: ['tenant-mappings', variables.tenantName, entityType],
      })
      // Invalidate all tenant mappings for this entity type to refresh both sides
      queryClient.invalidateQueries({
        queryKey: ['tenant-mappings'],
        predicate: (query) => {
          const [key1, , key3] = query.queryKey
          return key1 === 'tenant-mappings' && key3 === entityType
        },
      })
      // Invalidate MRD grid data to refresh the alignment
      queryClient.invalidateQueries({
        queryKey: ['crossroads-entity-grid', entityType],
      })
      // Invalidate companies data if it's a company type
      if (entityType === 'company' || entityType === 'counterparty') {
        queryClient.invalidateQueries({
          queryKey: ['crossroads-network-companies-3way'],
        })
      }
    },
    onError: (error: Error) => {
      message.error(`Failed to remove mapping: ${error.message}`)
    },
  })

  return {
    createMappingMutation,
    removeMappingMutation,
  }
}
